'use client';

import { Check, Copy } from '@phosphor-icons/react/ssr';
import { useEffect, useRef, useState } from 'react';

import { tv } from '../../lib/tv';

export type CodeSnippetProps = {
  /** The command shown and copied. */
  command: string;
  /** Leading prompt glyph, not copied. Default "$". */
  prompt?: string;
  /** Accessible name of the copy button. Default "Copy command". */
  copyLabel?: string;
  className?: string;
};

/**
 * Figma "Code snippet" (110:417, Landing Page ↳ Agents). bg/surface, 1px
 * border/subtle (in layout), radius/lg, 11px top/bottom · 16px left · 14px
 * right, gap 12. Prompt text/tertiary, command text/primary, both Geist Mono
 * Medium 14/20. 16px copy icon in icon/primary.
 *
 * Geist Mono isn't bundled: the stack falls back to the platform monospace.
 */
export const codeSnippetStyles = tv({
  slots: {
    root: [
      'bg-bg-surface border-border-subtle flex w-fit max-w-full items-center gap-3 rounded-lg border py-[11px] pr-3.5 pl-4',
      "text-body-md font-[family-name:'Geist_Mono',ui-monospace,SFMono-Regular,Menlo,Consolas,monospace] font-medium",
    ],
    prompt: 'text-text-tertiary shrink-0 select-none',
    // Preflight gives <code> the default mono stack: inherit ours.
    command:
      'text-text-primary min-w-0 overflow-x-auto whitespace-nowrap [font-family:inherit]',
    copy: [
      'text-icon-primary relative flex shrink-0 cursor-pointer items-center justify-center rounded-xs outline-none',
      'transition-[color,box-shadow] duration-[120ms] ease-out hover:text-icon-secondary',
      'focus-visible:shadow-focus-ring',
      // 32px hit area around the 16px icon, without changing layout.
      'after:absolute after:-inset-2',
    ],
    icon: 'size-4',
  },
});

/** A copyable terminal command. Clicking the button copies it and says "Copied". */
export function CodeSnippet({
  command,
  prompt = '$',
  copyLabel = 'Copy command',
  className,
}: CodeSnippetProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const styles = codeSnippetStyles();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
    } catch {
      return; // No clipboard (insecure context / denied): leave the state alone.
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  const Icon = copied ? Check : Copy;
  return (
    <div data-slot="code-snippet" className={styles.root({ className })}>
      {prompt && (
        <span aria-hidden className={styles.prompt()}>
          {prompt}
        </span>
      )}
      <code className={styles.command()}>{command}</code>
      <button
        type="button"
        aria-label={copyLabel}
        data-copied={copied || undefined}
        className={styles.copy()}
        onClick={copy}
      >
        <Icon aria-hidden className={styles.icon()} />
      </button>
      <span role="status" className="sr-only">
        {copied ? 'Copied' : ''}
      </span>
    </div>
  );
}
